import { useParams, useLocation, Link } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./Breadcrumb.module.scss";

interface IBreadcrumbItem {
  label: string;
  href?: string;
  isFolder?: boolean;
}

interface IBreadcrumbProps {
  items?: IBreadcrumbItem[];
  hierarchy?: Array<{ title: string; isFolder?: boolean }>;
  pageName?: string;
}

export function Breadcrumb({ items, hierarchy, pageName }: IBreadcrumbProps): ReactNode {
  const params = useParams();
  const location = useLocation();
  const locale = (params?.locale as string) || 'en-US';
  const pathname = location.pathname;
  const { t } = useTranslations();

  // Build breadcrumb items
  let breadcrumbItems: IBreadcrumbItem[];
  
  if (hierarchy && pageName) {
    breadcrumbItems = hierarchy.map((parent) => ({
      label: parent.title,
      href: undefined,
      isFolder: parent.isFolder
    }));
    // Add the current page as the last item
    breadcrumbItems.push({ label: pageName });
  } else if (items) {
    breadcrumbItems = items;
  } else {
    breadcrumbItems = generateBreadcrumbsFromPath(pathname, locale);
  }

  return (
    <nav className={styles.breadcrumb} aria-label={t("breadcrumb.ariaLabel")}>
      <ol className={styles.breadcrumbList}>
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          return (
            <li key={`${item.href || 'current'}-${item.label}`} className={styles.breadcrumbItem}>
              {!isLast && item.href ? (
                <Link to={item.href} className={styles.breadcrumbLink}>
                  {item.label}
                </Link>
              ) : (
                <span className={isLast ? styles.breadcrumbCurrent : styles.breadcrumbText} aria-current={isLast ? "page" : undefined}>
                  {item.label}
                </span>
              )}
              {!isLast && <span className={styles.separator} aria-hidden="true">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function generateBreadcrumbsFromPath(pathname: string, locale: string): IBreadcrumbItem[] {
  const items: IBreadcrumbItem[] = [];
  
  // Remove locale from pathname
  const pathWithoutLocale = pathname.replace(`/${locale}`, '');
  const segments = pathWithoutLocale.split('/').filter(Boolean);
  
  // Build breadcrumbs from path segments
  let currentPath = `/${locale}`;
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const label = decodeURIComponent(segment)
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    items.push({
      label,
      href: index < segments.length - 1 ? currentPath : undefined
    });
  });
  
  return items;
}
